import { matchesFieldPattern, type TaxonomyField } from '@/entities/taxonomy';
import type { TaxonomyRecordCells } from '@/entities/taxonomy-dataset';
import type { GridColumn } from './columns';
import type { EffectiveField, EffectiveFields } from './applyRules';

/** Why a cell is not acceptable as it stands. */
export type CellDefect = 'required_empty' | 'breaks_pattern';

/** Anything a cell can hold. A multiselect holds several values. */
type CellValue = TaxonomyRecordCells[string];

/** Whether the field's value must come from a vocabulary. */
function isConstrained(field: TaxonomyField): boolean {
  return field.type === 'dropdown' || Boolean(field.listRef?.listId);
}

/**
 * The scalar a cell holds, as the CELL ITSELF reads it.
 *
 * An array collapses to its first entry, because that is what the grid renders
 * (`raw[0] ?? ''`) and what the pattern check has always tested. Reading every
 * entry instead — which is how the export side used to do it — makes
 * `['', 'x']` "filled" while the cell displays an em dash, so the user sees an
 * empty required cell with nothing marking it.
 *
 * One reading, used by both checks below. Two readings in one predicate is how
 * `['', 'abc']` came out as neither empty nor pattern-breaking.
 */
function scalar(raw: CellValue): string {
  return Array.isArray(raw) ? (raw[0] ?? '') : (raw ?? '');
}

/**
 * Whether a cell holds nothing.
 *
 * Whitespace is deliberately NOT empty, matching the behaviour this replaces: a
 * value of `'   '` is a defect nobody currently reports, and inventing one here
 * would start blocking saves that work today.
 */
function isBlank(raw: CellValue): boolean {
  return !scalar(raw);
}

/**
 * Whether a value fails the field's own pattern.
 *
 * Moved out of `TaxonomyRecordGrid` so the marker and the Save gate cannot drift:
 * a pattern-breaking value can never mint a tracking code, so both the grid and
 * the export warning have to see it.
 */
function breaksPattern(field: TaxonomyField, raw: CellValue): boolean {
  const value = scalar(raw);
  if (!value || !field.validationPattern) {
    return false;
  }
  return !matchesFieldPattern(value, field.validationPattern);
}

/**
 * What is wrong with one cell, or null.
 *
 * THE one place the four rules live — which columns count, what counts as empty,
 * how an array cell reads, whether `required` gates. Two callers previously
 * re-derived all four out of `applyRules` and disagreed on every one of them, so
 * the grid could block a Save the export dialog called clean and the reverse.
 *
 * Non-editable columns are skipped, and that is not tidiness. A `computed` field
 * can be marked required — the builder's field editor never filtered by type —
 * and gating Save on a defect in a cell nobody can type into leaves no way out of
 * the grid except discarding the edit.
 */
export function cellDefect(
  column: GridColumn,
  state: EffectiveField | undefined,
  raw: CellValue,
): CellDefect | null {
  if (!column.editable || state?.hidden) {
    return null;
  }
  /*
   * A cell with nothing to choose from cannot be a defect either.
   *
   * Excluding `computed` and `linked` above covers the columns nobody can type
   * into by TYPE. This covers the ones nobody can fill by STATE, which is the same
   * deadlock by a different route: a required dropdown published with no values
   * (`validateSchema` rates that a warning, so it ships), two `limit_options`
   * branches whose sets are individually valid and jointly empty, or a list-bound
   * column whose list resolved to nothing. In each case the editor offers zero
   * options, so reporting the cell would disable Save with nothing the user could
   * do about it.
   *
   * A free-text column is not affected: it has no vocabulary, so `options` being
   * empty says nothing about whether it can be filled.
   */
  if (isConstrained(column.field) && (state?.options.length ?? 0) === 0) {
    return null;
  }
  if (breaksPattern(column.field, raw)) {
    return 'breaks_pattern';
  }
  if (state?.required && isBlank(raw)) {
    return 'required_empty';
  }
  return null;
}

/** Every defect in one row, by field id. An empty map means the row is complete. */
export function rowDefects(
  columns: readonly GridColumn[],
  effective: EffectiveFields,
  cells: TaxonomyRecordCells,
): Map<string, CellDefect> {
  const defects = new Map<string, CellDefect>();
  for (const column of columns) {
    const defect = cellDefect(column, effective[column.field.id], cells[column.field.id]);
    if (defect) {
      defects.set(column.field.id, defect);
    }
  }
  return defects;
}

/**
 * Whether a row is fit to save.
 *
 * Stops at the first defect, so a defective row is cheap and a clean one pays
 * every column. That is the wrong way round for the common case, and deliberately
 * not optimised: the caller is a memo over dirty rows only, so the work is bounded
 * by what someone edited rather than by the 20 000-record cap.
 */
export function isRowComplete(
  columns: readonly GridColumn[],
  effective: EffectiveFields,
  cells: TaxonomyRecordCells,
): boolean {
  return !columns.some((column) =>
    cellDefect(column, effective[column.field.id], cells[column.field.id]),
  );
}
